import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";

const DRIFTING = [
  { code: "import numpy as np",               x: "8%",  y: "15%", dur: 22, delay: 0    },
  { code: "model.fit(X_train, y_train)",       x: "72%", y: "10%", dur: 28, delay: 3    },
  { code: "def predict(x): ...",               x: "18%", y: "75%", dur: 24, delay: 6    },
  { code: "accuracy = 97.3%",                  x: "78%", y: "70%", dur: 20, delay: 1.5  },
  { code: "for epoch in range(100):",          x: "5%",  y: "50%", dur: 30, delay: 4    },
  { code: "loss.backward()",                   x: "82%", y: "45%", dur: 26, delay: 8    },
  { code: "X = pd.read_csv('data.csv')",       x: "40%", y: "85%", dur: 25, delay: 2    },
  { code: "print('Hello, Python!')",           x: "55%", y: "8%",  dur: 32, delay: 5    },
];

const CTABanner = () => {
  const monoFont = { fontFamily: "'JetBrains Mono', monospace" };
  const soraFont = { fontFamily: "'Sora', sans-serif" };

  return (
    <section style={{ backgroundColor: '#0A0F1E', ...soraFont }} className="relative py-24 md:py-48 overflow-hidden border-t border-[#1F2937]">

      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none select-none opacity-[0.4]">
        {DRIFTING.map((s, i) => (
          <motion.div
            key={i}
            style={{ left: s.x, top: s.y, ...monoFont }}
            className="absolute text-[clamp(9px,1.5vw,11px)] text-[#00E676]/10 whitespace-nowrap uppercase font-black tracking-widest"
            animate={{ y: [0, -18, 0], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: s.dur, repeat: Infinity, delay: s.delay, ease: "easeInOut" }}
          >
            {s.code}
          </motion.div>
        ))}
      </div>

      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#00E676 1px, transparent 1px), linear-gradient(90deg, #00E676 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <motion.div
        animate={{ scale: [1, 1.12, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#00E676]/5 rounded-full blur-[130px] pointer-events-none"
      />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 flex flex-col items-center text-center">

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-center gap-4 mb-10"
        >
          <div className="h-px w-10 bg-[#00E676]/20" />
          <span style={monoFont} className="text-[10px] font-black text-[#00E676] uppercase tracking-[0.4em]">
            Final Step
          </span>
          <div className="h-px w-10 bg-[#00E676]/20" />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={monoFont}
          className="text-[clamp(2.5rem,8vw,5.5rem)] font-black text-white leading-[1] tracking-tighter uppercase mb-8"
        >
          Your Python Journey <br />
          <span className="text-[#00E676] italic">Starts Today</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={soraFont}
          className="text-[#6B7280] text-lg leading-relaxed max-w-2xl mb-16"
        >
          Pick your path — learn free, go deep with the bootcamp, or visualize
          your code. Everything you need is here.
        </motion.p>

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full sm:w-auto"
        >
          <Link to="/tutorials" className="w-full sm:w-auto">
            <Button variant="outline" size="lg" className="w-full sm:w-auto h-20 px-12 text-lg">
              START FREE LEARNING
            </Button>
          </Link>

          <Link to="/bootcamp" className="w-full sm:w-auto">
            <Button size="lg" className="w-full sm:w-auto h-20 px-12 text-lg">
              JOIN THE BOOTCAMP →
            </Button>
          </Link>
        </motion.div>

        {/* Bottom Proof */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          style={monoFont}
          className="mt-20 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-12 gap-y-4 text-[#6B7280] text-[10px] font-black uppercase tracking-[0.3em]"
        >
          <span className="flex items-center gap-3">
            <CheckCircle2 className="w-4 h-4 text-[#00E676]/40" />
            No Signup Required
          </span>
          <span className="flex items-center gap-3">
            <CheckCircle2 className="w-4 h-4 text-[#00E676]/40" />
            Lifetime Access
          </span>
        </motion.div>

      </div>
    </section>
  );
};

export default CTABanner;
